<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="profile-page-title" v-if="isMyProfile">
      {{ $t('profile.page_title_own') }}
    </h4>
    <h4 qid="profile-page-title" v-if="!isMyProfile">
      {{ $t('profile.page_title') }}
    </h4>

    <!-- Main panel -->
    <div class="card">
      <div class="card-body">
        <div class="row justify-content-between">
          <div class="col-4 d-flex align-items-center">
            <div class="mr-4">
              <div class="logo-container">
                <img v-bind:src="getProfileAvatar"
                     class="rounded-circle"
                     width="120"
                     height="120"
                     qid="profile-photo"
                >
                <div class="upload-helper rounded-circle" v-if="isMyProfile">
                  <label class="mr-3 upload">
                    <input type="file"
                           name="photo"
                           value="photo"
                           id="file-photo"
                           @change="changePhoto"
                           @click="resetFile"
                    >
                    <font-awesome-icon icon="cloud-upload-alt" class="cursor-pointer" />
                  </label>
                  <span @click="removePhoto">
                    <font-awesome-icon icon="trash-alt" class="cursor-pointer"/>
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h5 qid="profile-name">
                {{ user.name }}
              </h5>
              <h6 class="larger-text" qid="profile-email">
                <span class="text-muted mr-2">
                  <font-awesome-icon icon="envelope" />
                </span>
                {{ user.email }}
              </h6>
              <h6 class="larger-text" qid="profile-phone">
                <span class="text-muted mr-2">
                  <font-awesome-icon icon="phone" />
                </span>
                {{ user.phone }}
              </h6>
            </div>
          </div>
          <div class="col-4 text-right">
            <b-button
                variant="secondary"
                class=""
                qid="change-password-button"
                @click="showChangePasswordModal"
                v-if="isGranted([permissions.USER_EDIT, permissions.USER_EDIT_OWN], user) && isMyProfile && user.authentication != 'Microsoft' && user.authentication != 'Google'"
            >
              {{ $t('profile.change_password_button') }}
            </b-button>
            <b-button
                variant="success"
                class="ml-2"
                qid="edit-profile-button"
                @click="showEditProfileModal"
                v-if="isGranted([permissions.USER_EDIT, permissions.USER_EDIT_OWN], user) && isMyProfile"
            >
              {{ $t('profile.edit_profile_button') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <b-tabs class="mt-3">

      <!-- First tab -->
      <b-tab :title="$t('profile.activity_tab_title')" class="mt-3">
        <div class="card" v-if="showActivityTab">
          <div class="card-body">
            <div>
              <b-list-group>
                <b-list-group-item
                    v-for="activity in items"
                    :key="activity.id"
                    class="border-left-0 border-right-0 border-top border-bottom-0 pt-4 pb-4"
                >

                  <ProcessActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Process\\Models\\Process'"/>
                  <ProcessGdprActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Process\\Models\\ProcessGdpr'"/>
                  <ProcessPolicyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Process\\Models\\ProcessPolicy'"/>
                  <ProcessLegalBasisActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Process\\Models\\ProcessLegalBasis'"/>

                  <SystemActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\System'"/>
                  <SystemPolicyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemPolicy'"/>
                  <SystemNetworkActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemNetwork'"/>
                  <SystemLogManagementActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemLogManagement'"/>
                  <SystemDataObjectActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemDataObject'"/>
                  <SystemDataDiscoveryActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemDataDiscovery'"/>
                  <SystemAccessControlActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemAccessControl'"/>
                  <SystemThirdPartyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemThirdParty'"/>

                  <ActivityActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\Activity'"/>
                  <ActivityGdprActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityGdpr'"/>
                  <ActivityPolicyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityPolicy'"/>
                  <ActivityDataSubjectActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityDataSubject'"/>
                  <ActivityIntegrityControlActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityIntegrityControl'"/>
                  <ActivityOrganizationActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityOrganization'"/>
                  <ActivityRetentionRuleActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityRetentionRule'"/>
                  <ActivitySystemActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivitySystem'"/>
                  <ActivityThirdPartyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityThirdParty'"/>

                  <ValidationActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Validation\\Models\\Validation'"/>

                  <ActivityProjectActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Project\\Models\\Project'"/>

                  <FrameworkActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\Framework\\Models\\Framework'"/>
                  <FrameworkControlObjectiveActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ControlObjective\\Models\\ControlObjective'"/>
                  <FrameworkControlActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\FrameworkControl\\Models\\FrameworkControl'"/>


                  <RiskScenarioActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\RiskScenario\\Models\\RiskScenario'"/>
                  <RiskScenarioThreatActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\RiskScenario\\Models\\RiskScenarioThreat'"/>
                  <RiskScenarioVulnerabilityActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\RiskScenario\\Models\\RiskScenarioVulnerability'"/>

                  <ThirdPartyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                  <ThirdPartyDataObjectActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyDataObject'"/>
                  <ThirdPartyRiskScenarioActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyRiskScenario'"/>
                  <SystemRiskScenarioActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemRiskScenario'"/>
                  <ActivityRiskScenarioActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityRiskScenario'"/>
                  <ProcessRiskScenarioActivity :activity="activity" :changes="true" v-if="activity.subject_type === 'App\\Process\\Models\\ProcessRiskScenario'"/>

                  <GenericControlActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\GenericControl\\Models\\GenericControl'"/>
                  <GenericControlFrameworkActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\GenericControl\\Models\\GenericControlFramework'"/>
                  <GenericControlRiskScenarioActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\GenericControl\\Models\\GenericControlRiskScenario'"/>
                  <GenericControlPolicyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\GenericControl\\Models\\GenericControlPolicy'"/>
                  <GenericControlThreatActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\GenericControl\\Models\\GenericControlThreat'"/>
                  <GenericControlVulnerabilityActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\GenericControl\\Models\\GenericControlVulnerability'"/>

                  <AssessmentActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\Assessment\\Models\\Assessment'"/>
                  <QuestionActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\Assessment\\Models\\Question'"/>

                  <ProjectModelActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Project\\Models\\ProjectModel'"/>
                  <ProjectTaskActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Project\\Models\\ProjectTask'"/>

                  <MeasureActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\Measure\\Models\\Measure'"/>
                  <ActivityMeasureActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Activity\\Models\\ActivityMeasure'"/>
                  <SystemMeasureActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\System\\Models\\SystemMeasure'"/>
                  <ThirdPartyMeasureActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyMeasure'"/>
                  <GenericControlMeasureActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\GenericControl\\Models\\GenericControlMeasure'"/>
                  <VendorActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\Vendor'"/>
                  <ThirdPartyDocumentActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyDocument'"/>
                  <PolicyActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\Policy\\Models\\Policy'"/>
                  <PolicyValidationActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Validation\\Models\\Validation'"/>
                  <PolicyDocumentActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\Policy\\Models\\PolicyDocument'"/>

                  <ThirdPartyContactActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyContact'"/>
                  <ThirdPartySubdataProcessorActivity :changes="true" :activity="activity" v-if="activity.subject_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdPartySubdataProcessor'"/>


                </b-list-group-item>

              </b-list-group>
              <hr class="mt-0"/>
              <InfiniteLoading @infinite="loadingHandler" v-if="getClient && getClient.slug">
                <template slot="no-results">
                  <span>
                    {{ $t('system.no_results') }}
                  </span>
                </template>
              </InfiniteLoading>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- Second tab -->
      <b-tab :title="$t('profile.settings_tab_title')"
             class="mt-3"
             v-if="isGranted([permissions.USER_DELETE, permissions.USER_DELETE_OWN], user) && isMyProfile"
      >
        <div class="card">
          <div class="card-body">
            <div class="col-lg-5 col-md-6 col-sm-6">
              <h6>
                {{ $t('profile.settings.theme_label') }}:
                <multiselect
                    class="mt-2"
                    v-model="selectedTheme"
                    :options="themeOptions"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    track-by="value"
                    label="title"
                    @select="selectTheme"
                    :placeholder="$t('profile.settings.theme_placeholder')"
                    :deselect-label="$t('profile.settings.theme_deselect')"
                    :allow-empty="false"
                ></multiselect>
              </h6>
              <h6>
                {{ $t('profile.settings.language_label') }}:
                <multiselect
                    class="mt-2"
                    v-model="selectedLang"
                    :multiple="true"
                    :taggable="false"
                    :limit="1"
                    :limitText="count => ` `"
                    :options="langOptions"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    track-by="value"
                    label="title"
                    @select="selectLang"
                    :placeholder="$t('profile.settings.language_placeholder')"
                    :deselect-label="$t('profile.settings.language_deselect')"
                    :allow-empty="false"
                >
                  <template slot="tag" slot-scope="props">
                    <img width="25px" height="25px" src="@/assets/en.png" class="ml-2" v-if="props.option.value == 'en'">
                    <img width="25px" height="25px" src="@/assets/da.png" class="ml-2" v-if="props.option.value == 'da'">
                    <img width="25px" height="25px" src="@/assets/fr.png" class="ml-2" v-if="props.option.value == 'fr'">
                    <span class="ml-1">
                      {{ props.option.title }}
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <img width="25px" height="25px" src="@/assets/en.png" class="ml-2" v-if="props.option.value == 'en'">
                    <img width="25px" height="25px" src="@/assets/da.png" class="ml-2" v-if="props.option.value == 'da'">
                    <img width="25px" height="25px" src="@/assets/fr.png" class="ml-2" v-if="props.option.value == 'fr'">
                    <span class="ml-1">
                      {{ props.option.title }}
                    </span>
                  </template>
                </multiselect>
              </h6>
              <div class="pt-3 pb-3" v-if="projectModule">
                <h6>
                  {{ $t('system.global_guide_label') }}:
                </h6>

                <div v-if="projectModule">
                  <b-form-checkbox
                    v-model="settingsForm.hide_update_task_guide"
                    name="checkbox-disable"
                    :value="1"
                    @change="updateSettings"
                    :unchecked-value="0"
                  >
                    {{ $t('system.hide_update_task_guide') }}
                  </b-form-checkbox>
                  <br>
                </div>

                <div v-if="projectModule">
                  <b-form-checkbox
                    v-model="settingsForm.hide_validation_task_guide"
                    name="checkbox-disable"
                    :value="1"
                    @change="updateSettings"
                    :unchecked-value="0"
                  >
                    {{ $t('system.hide_validation_task_guide') }}
                  </b-form-checkbox>
                  <br>
                </div>

                <div v-if="riskModule">
                  <b-form-checkbox
                    v-model="settingsForm.hide_control_task_guide"
                    name="checkbox-disable"
                    :value="1"
                    @change="updateSettings"
                    :unchecked-value="0"
                  >
                    {{ $t('system.hide_control_task_guide') }}
                  </b-form-checkbox>
                  <br>
                </div>

                <div v-if="assessmentModule">
                  <b-form-checkbox
                    v-model="settingsForm.hide_assessment_task_guide"
                    name="checkbox-disable"
                    :value="1"
                    @change="updateSettings"
                    :unchecked-value="0"
                  >
                    {{ $t('system.hide_assessment_task_guide') }}
                  </b-form-checkbox>
                  <br>
                </div>

                <span class="text-muted">
                  * {{ $t('system.global_guide_explainer_text') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

    </b-tabs>

    <!-- Edit profile modal template -->
    <b-modal ref="edit-profile" hide-footer>
      <div class="d-block text-center">
        <h4 qid="edit-profile-modal-title">
          {{ $t('profile.edit.title') }}
        </h4>
      </div>
      <b-alert v-if="userForm.error && userForm.error.length"
               show
               variant="danger"
               qid="edit-user-error"
      >
        {{ userForm.error }}
      </b-alert>
      <b-form @submit="userFormSubmit" qid="login-form">
        <div class="form-container">
          <b-form-group>
            <span class="align-middle mr-4">
              <img v-bind:src="getAvatar"
                   class="rounded-circle"
                   width="120"
                   height="120"
                   qid="profile-photo"
              >
            </span>
            <span class="align-middle">
              <label class="btn btn-secondary cursor-pointer pl-2 pr-2">
                <input type="file"
                       name="fileupload"
                       value="fileupload"
                       id="file"
                       @change="onFileChange"
                >
                <span>
                  <font-awesome-icon icon="cloud-upload-alt" />
                  {{ $t('profile.edit.upload_picture') }}
                </span>
              </label>
            </span>
          </b-form-group>
          <b-form-group>
            <label qid="edit-profile-name-label">
              {{ $t('profile.edit.full_name_label') }}
            </label>
            <b-form-input
              qid="user-form-name"
              type="text"
              v-model="userForm.name"
              required
              :placeholder="$t('profile.edit.full_name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-profile-phone-label">
              {{ $t('profile.edit.phone_label') }}
            </label>
            <b-form-input
              type="text"
              qid="user-form-phone"
              v-model="userForm.phone"
              :placeholder="$t('profile.edit.phone_placeholder')"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditProfileModal"
                    qid="edit-profile-cancel-button"
          >
            {{ $t('profile.password.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-profile-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('profile.password.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Change password modal template -->
    <b-modal ref="change-password" hide-footer>
      <div class="d-block text-center">
        <h4 qid="change-password-modal-title">
          {{ $t('profile.password.page_title') }}
        </h4>
      </div>
      <b-alert v-if="showPasswordError"
               show
               variant="danger"
               qid="change-password-form-error"
      >
        {{ passwordError }}
      </b-alert>
      <b-form @submit="resetPasswordFormSubmit" qid="change-password-form">
        <div class="form-container">
          <b-form-group>
            <label qid="change-password-current-label">
              {{ $t('profile.password.current_label') }}
            </label>
            <b-form-input
              type="password"
              qid="input-change-old-password"
              v-model="passwordForm.password"
              required
              :placeholder="$t('profile.password.current_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="change-password-new-label">
              {{ $t('profile.password.new_label') }}
            </label>
            <b-form-input
              type="password"
              qid="input-change-new-password"
              v-model="passwordForm.new_password"
              required
              :state="validationPass"
              :placeholder="$t('profile.password.new_placeholder')"
            ></b-form-input>
            <b-form-invalid-feedback
                :state="validationPass"
                qid="pasword-requirement-notice"
                class="text-center"
            >
              {{ $t('reset_password.notice.password_requirement') }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group>
            <label qid="change-password-retype-new-label">
              {{ $t('profile.password.retype_new_label') }}
            </label>
            <b-form-input
              type="password"
              qid="input-change-repeat-new-password"
              v-model="passwordForm.new_password_confirmation"
              required
              :state="validationRepeatPass"
              :placeholder="$t('profile.password.retype_new_placeholder')"
            ></b-form-input>
            <b-form-invalid-feedback
                :state="validationRepeatPass"
                qid="pasword-dont-match-notice"
                class="text-center"
            >
              {{ $t('reset_password.notice.do_not_match') }}
            </b-form-invalid-feedback>
            <b-form-valid-feedback
              :state="validationRepeatPass"
              class="text-center"
              v-if="passwordForm.new_password_confirmation && passwordForm.new_password_confirmation.length"
              qid="pasword-looks-good-notice"
            >
              {{ $t('reset_password.notice.looks_good') }}
            </b-form-valid-feedback>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideChangePasswordModal"
                    qid="change-password-cancel-button"
          >
            {{ $t('profile.password.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="change-password-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('profile.password.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'

import ProcessActivity from '@/components/activity/process/Process.vue';
import ProcessGdprActivity from '@/components/activity/process/ProcessGdpr.vue';
import ProcessPolicyActivity from '@/components/activity/process/ProcessPolicy.vue';
import ProcessLegalBasisActivity from '@/components/activity/process/ProcessLegalBasis.vue';

import SystemActivity from '@/components/activity/system/System.vue';
import SystemPolicyActivity from '@/components/activity/system/SystemPolicy.vue';
import SystemNetworkActivity from '@/components/activity/system/SystemNetwork.vue';
import SystemLogManagementActivity from '@/components/activity/system/SystemLogManagement.vue';
import SystemDataObjectActivity from '@/components/activity/system/SystemDataObject.vue';
import SystemDataDiscoveryActivity from '@/components/activity/system/SystemDataDiscovery.vue';
import SystemAccessControlActivity from '@/components/activity/system/SystemAccessControl.vue';
import SystemThirdPartyActivity from '@/components/activity/system/SystemThirdParty.vue';

import ActivityActivity from '@/components/activity/activity/Activity.vue';
import ActivityGdprActivity from '@/components/activity/activity/ActivityGdpr.vue';
import ActivityPolicyActivity from '@/components/activity/activity/ActivityPolicy.vue';
import ActivityDataSubjectActivity from '@/components/activity/activity/ActivityDataSubject.vue';
import ActivityIntegrityControlActivity from '@/components/activity/activity/ActivityIntegrityControl.vue';
import ActivityOrganizationActivity from '@/components/activity/activity/ActivityOrganization.vue';
import ActivityRetentionRuleActivity from '@/components/activity/activity/ActivityRetentionRule.vue';
import ActivitySystemActivity from '@/components/activity/activity/ActivitySystem.vue';
import ActivityThirdPartyActivity from '@/components/activity/activity/ActivityThirdParty.vue';

import ValidationActivity from '@/components/activity/validation/Validation.vue';

import ActivityProjectActivity from '@/components/activity/activity/ActivityProject.vue';

import FrameworkActivity from '@/components/activity/framework/Framework.vue';
import FrameworkControlObjectiveActivity from '@/components/activity/framework/FrameworkControlObjective.vue';
import FrameworkControlActivity from '@/components/activity/framework/FrameworkControl.vue';

import RiskScenarioActivity from '@/components/activity/risk/RiskScenario.vue';
import RiskScenarioThreatActivity from '@/components/activity/risk/RiskScenarioThreat.vue';
import RiskScenarioVulnerabilityActivity from '@/components/activity/risk/RiskScenarioVulnerability.vue';
import RiskScenarioValidationActivity from '@/components/activity/risk/RiskScenarioValidation.vue';

import ThirdPartyActivity from '@/components/activity/thirdparty/ThirdParty.vue';
import ThirdPartyDataObjectActivity from '@/components/activity/thirdparty/ThirdPartyDataObject.vue';

import ThirdPartyRiskScenarioActivity from '@/components/activity/thirdparty/ThirdPartyRiskScenario.vue';
import SystemRiskScenarioActivity from '@/components/activity/system/SystemRiskScenario.vue';
import ActivityRiskScenarioActivity from '@/components/activity/activity/ActivityRiskScenario.vue';
import ProcessRiskScenarioActivity from '@/components/activity/process/ProcessRiskScenario.vue';

import GenericControlActivity from '@/components/activity/genericcontrol/GenericControl.vue';
import GenericControlFrameworkActivity from '@/components/activity/genericcontrol/GenericControlFramework.vue';
import GenericControlRiskScenarioActivity from '@/components/activity/genericcontrol/GenericControlRiskScenario.vue';
import GenericControlPolicyActivity from '@/components/activity/genericcontrol/GenericControlPolicy.vue';
import GenericControlThreatActivity from '@/components/activity/genericcontrol/GenericControlThreat.vue';
import GenericControlVulnerabilityActivity from '@/components/activity/genericcontrol/GenericControlVulnerability.vue';

import AssessmentActivity from '@/components/activity/assessment/Assessment.vue';
import QuestionActivity from '@/components/activity/assessment/Question.vue';

import ProjectModelActivity from '@/components/activity/project/ProjectModel.vue';
import ProjectTaskActivity from '@/components/activity/project/ProjectTask.vue';
import MeasureActivity from '@/components/activity/measure/Measure.vue';
import ActivityMeasureActivity from '@/components/activity/activity/ActivityMeasure.vue';
import SystemMeasureActivity from '@/components/activity/system/SystemMeasure.vue';
import ThirdPartyMeasureActivity from '@/components/activity/thirdparty/ThirdPartyMeasure.vue';
import GenericControlMeasureActivity from '@/components/activity/genericcontrol/GenericControlMeasure.vue';
import InfiniteLoading from 'vue-infinite-loading';
import VendorActivity from '@/components/activity/thirdparty/Vendor.vue';
import ThirdPartyDocumentActivity from '@/components/activity/thirdparty/ThirdPartyDocument.vue';

import PolicyActivity from '@/components/activity/policy/Policy.vue';
import PolicyValidationActivity from '@/components/activity/policy/PolicyValidation.vue';
import PolicyDocumentActivity from '@/components/activity/policy/PolicyDocument.vue';
import ThirdPartyContactActivity from '@/components/activity/thirdparty/ThirdPartyContact.vue';
import ThirdPartySubdataProcessorActivity from '@/components/activity/thirdparty/ThirdPartySubdataProcessor.vue';
export default {
  name: 'Profile',
  data: function() {
    return {
      user: {},
      userForm: {
        name: '',
        email: '',
        phone: '',
        file: {}
      },
      passwordForm: {
        password: '',
        new_password: '',
        new_password_confirmation: ''
      },
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        }
      ],
      showActivityTab: false,
      showPasswordError: false,
      passwordError: '',
      avatar: '',
      avatarLarge: '',
      selectedTheme: {
        value: config.theme,
        title: config.theme.charAt(0).toUpperCase() + config.theme.slice(1)
      },
      selectedLang: [],
      themeOptions: [
        {value: 'complyon', title: 'Complyon'},
        {value: 'light', title: 'Light'},
        {value: 'dark', title: 'Dark'}
      ],
      langOptions: [
        {value: 'en', title: 'English'},
        {value: 'da', title: 'Dansk'},
        {value: 'fr', title: 'French'}
      ],
      items: [],
      currentPage: 1,
      totalRows: 0,
      isMyProfile: false,
      buttonDisabled: false,
      page: 1,
      settingsForm: {
        hide_update_task_guide: this.$store.state.user.hide_update_task_guide,
        hide_validation_task_guide: this.$store.state.user.hide_validation_task_guide,
        hide_control_task_guide: this.$store.state.user.hide_control_task_guide,
        hide_assessment_task_guide: this.$store.state.user.hide_assessment_task_guide
      },
      projectModule: false,
      riskModule: false,
      assessmentModule: false,
    }
  },
  components: {
    ProcessActivity,
    ProcessGdprActivity,
    ProcessPolicyActivity,
    ProcessLegalBasisActivity,
    SystemActivity,
    SystemPolicyActivity,
    SystemNetworkActivity,
    SystemLogManagementActivity,
    SystemDataObjectActivity,
    SystemDataDiscoveryActivity,
    SystemAccessControlActivity,
    SystemThirdPartyActivity,
    ActivityActivity,
    ActivityPolicyActivity,
    ActivityDataSubjectActivity,
    ActivityIntegrityControlActivity,
    ActivityOrganizationActivity,
    ActivityRetentionRuleActivity,
    ActivitySystemActivity,
    ActivityThirdPartyActivity,
    ActivityGdprActivity,
    ValidationActivity,
    ActivityProjectActivity,
    FrameworkActivity,
    FrameworkControlObjectiveActivity,
    FrameworkControlActivity,
    RiskScenarioActivity,
    RiskScenarioThreatActivity,
    RiskScenarioVulnerabilityActivity,
    RiskScenarioValidationActivity,
    ThirdPartyActivity,
    ThirdPartyDataObjectActivity,
    InfiniteLoading,
    ThirdPartyRiskScenarioActivity,
    SystemRiskScenarioActivity,
    ActivityRiskScenarioActivity,
    ProcessRiskScenarioActivity,
    GenericControlActivity,
    GenericControlFrameworkActivity,
    GenericControlRiskScenarioActivity,
    GenericControlPolicyActivity,
    GenericControlThreatActivity,
    GenericControlVulnerabilityActivity,
    AssessmentActivity,
    QuestionActivity,
    ProjectModelActivity,
    ProjectTaskActivity,
    MeasureActivity,
    ActivityMeasureActivity,
    SystemMeasureActivity,
    ThirdPartyMeasureActivity,
    GenericControlMeasureActivity,
    VendorActivity,
    ThirdPartyDocumentActivity,
    PolicyActivity,
    PolicyValidationActivity,
    PolicyDocumentActivity,
    ThirdPartyContactActivity,
    ThirdPartySubdataProcessorActivity
  },
  computed: {
    validationPass() {
      let containsCapital = RegExp('[A-Z]');
      let containsSpecial = RegExp('\\W');

      return this.passwordForm.new_password && this.passwordForm.new_password.length > 7 && containsCapital.test(this.passwordForm.new_password) && containsSpecial.test(this.passwordForm.new_password);
    },
    validationRepeatPass() {
      return this.passwordForm.new_password_confirmation && this.passwordForm.new_password === this.passwordForm.new_password_confirmation;
    },
    getAvatar(){
      if (this.avatarLarge) {
        return this.avatarLarge;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    getProfileAvatar(){
      let user = this.user;
      if (user.avatar_large) {
        return user.avatar_large;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    getClient() {
      return this.$store.state.client
    },
    getUser() {
      return this.$store.state.user
    },
    getUserClients() {
      return this.$store.state.user.clients
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (client) {
      if (client) {
        this.loadUser();
      }
    }
  },
  methods: {
    getProfile() {
      return this.user
    },
    updateSettings() {
      let self = this
      piincHttp.put('users/' + this.getUser.slug, this.settingsForm).then(function(response) {
        let user = self.getUser
        user.hide_update_task_guide = response.data.hide_update_task_guide
        user.hide_validation_task_guide = response.data.hide_validation_task_guide
        user.hide_control_task_guide = response.data.hide_control_task_guide
        user.hide_assessment_task_guide = response.data.hide_assessment_task_guide
        self.$store.commit('storeUser', user);
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    loadingHandler($state) {
      let self = this

      piincHttp.get('activity-log', { params:
      {
          user: this.user.id,
          page: this.page,
          client: this.getClient.slug
      }
      }).then(({ data }) => {
        if (data.items.length) {
          self.page += 1;
          self.items.push(...data.items);
          if ($state) {
            $state.loaded();
          }
        } else {
          if ($state) {
            $state.complete();
          }
        }
      });
    },
    onFileChange(file) {
      this.userForm.file = file;
      const avatar = file.target.files[0];
      this.avatar = URL.createObjectURL(avatar);
      this.avatarLarge = URL.createObjectURL(avatar);
    },
    selectTheme(value) {
      localStorage.setItem('theme', value.value)
      window.location.reload()
    },
    selectLang(value) {
      this.selectedLang = []
      this.selectedLang.push(value)
      localStorage.setItem('lang', value.value)
      window.location.reload()
    },
    removePhoto() {
      let self = this;
      let formData = new FormData();
      formData.append("avatar", null);
      formData.append("_method", 'put');

      piincHttp.post('users/' + this.user.slug, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(function(response) {
        if (self.isMyProfile) {
          // Store returned user in store if my profile
          response.data.clients = self.getUserClients;
          self.$store.commit('storeUser', response.data);
        }
        self.avatar = '';
        self.user = response.data
      }, function(error) {})
    },
    resetFile() {
      let imagefile = document.querySelector('#file-photo');
      imagefile.value = '';
    },
    changePhoto(file) {
      let self = this;
      let sizeError = false;
      let formData = new FormData();
      let imagefile = document.querySelector('#file-photo');
      const avatar = imagefile.files[0];
      this.avatar = URL.createObjectURL(avatar);
      this.avatarLarge = URL.createObjectURL(avatar);

      // Equal to 10 MB
      if (imagefile.files[0].size > 10485760) {
        sizeError = true;
      }

      formData.append("avatar", imagefile.files[0]);
      formData.append("_method", 'put');

      if (sizeError) {
        this.error = this.$t('system.file_size')
        alert(this.error)
      } else {
        piincHttp.post('users/' + this.user.slug, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          if (self.isMyProfile) {
            response.data.clients = self.getUserClients;
            // Store returned user in store if my profile
            self.$store.commit('storeUser', response.data);
          }
          self.user = response.data
        }, function(error) {
          self.buttonDisabled = false;
          self.userForm.error = error.data.message;
          if (error.data.errors.avatar) {
            self.userForm.error += ' ' + error.data.errors.avatar[0]
          }
          alert(self.userForm.error)
        })
      }
    },
    showEditProfileModal() {
      this.userForm.name = this.user.name;
      this.userForm.email = this.user.email;
      this.userForm.phone = this.user.phone;
      this.userForm.error = '';
      this.avatar = this.getProfileAvatar;
      this.avatarLarge = this.getProfileAvatar;
      this.$refs['edit-profile'].show()
    },
    hideEditProfileModal() {
      this.loadUser()
      this.$refs['edit-profile'].hide()
    },
    showChangePasswordModal() {
      this.$refs['change-password'].show()
    },
    hideChangePasswordModal() {
      this.showPasswordError = false;
      this.passwordForm.password = '';
      this.passwordForm.new_password = '';
      this.passwordForm.new_password_confirmation = '';
      this.$refs['change-password'].hide()
    },
    userFormSubmit(evt) {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      let sizeError = false;

      let formData = new FormData();
      let imagefile = document.querySelector('#file');

      // Update image only if there is one selected for uploaD
      if (imagefile.files && imagefile.files.length) {
        // Equal to 10 MB
        if (imagefile.files[0].size > 10485760) {
          sizeError = true;
        }
        formData.append("avatar", imagefile.files[0]);
      }

      formData.append("name", self.userForm.name);
      // Check for null value because formData is treating null as a string
      if (self.userForm.phone !== null) {
        formData.append("phone", self.userForm.phone);
      }

      formData.append("_method", 'put');

      if (sizeError) {
        this.userForm.error = this.$t('system.file_size')
        self.buttonDisabled = false;
      } else {
        piincHttp.post('users/' + this.user.slug, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          if (self.isMyProfile) {
            response.data.clients = self.getUserClients;
            // Store returned user in store if my profile
            self.$store.commit('storeUser', response.data);
          }
          if (response.data.slug != self.$route.params.slug) {
            self.$router.push({ name: 'profile', params: { slug: response.data.slug }})
          }
          self.buttonDisabled = false;
          self.hideEditProfileModal();
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.userForm.error = error.data.message;
            if (error.data.errors.avatar) {
              self.userForm.error += ' ' + error.data.errors.avatar[0]
            }
          }
        })
      }
    },
    resetPasswordFormSubmit(evt) {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();

      piincHttp.post('password/change', this.passwordForm).then(function(response) {
        if (self.isMyProfile) {
          response.data.clients = self.getUserClients;
          // Store returned user in store if my profile
          self.$store.commit('storeUser', response.data.user);
          localStorage.setItem('token', response.data.token);
          piincHttp.defaults.headers.common['Authorization'] = localStorage.getItem('token');
        }
        self.buttonDisabled = false;
        self.showPasswordError = false;
        self.hideChangePasswordModal();
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.showPasswordError = true;
          self.passwordError = error.data.message;
        }
      })
    },
    loadUser(){
      let self = this;
      piincHttp.get('users/' + this.$route.params.slug).then(function(response) {
        self.user = response.data;
        self.showActivityTab = true;
        if (self.breadcrumbItems && self.breadcrumbItems[1]) {
          self.breadcrumbItems.pop()
        }
        if (response.data.slug === self.$store.state.user.slug) {
          self.isMyProfile = true;
          self.breadcrumbItems.push({
            text: self.$t('profile.my_profile_label'),
            active: true
          })

          _.forEach(self.getUser.clients, function (client) {
            if (client.projectModule) {
              self.projectModule = true
            }
            if (client.riskModule) {
              self.riskModule = true
            }
            if (client.assessmentModule) {
              self.assessmentModule = true
            }
          })

        } else {
          self.isMyProfile = false;
          self.breadcrumbItems.push({
            text: self.$t('profile.user_profile_label'),
            active: true
          })
        }
      }, function() {});
    }
  },
  // Lifecycle hooks
  created() {
    let self = this;

    if (this.getClient && this.getClient.slug) {
      this.loadUser();
    }

    if (localStorage.getItem('lang')) {
      _.find(self.langOptions, function(item) {
        if (item.value === localStorage.getItem('lang')) {
          self.selectedLang = item;
        }
      });
    } else {
      this.selectedLang = this.langOptions[0]
    }

  }
}
</script>
